import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { FlowStep, getNewProjectSteps } from '../../data/flow-steps';
import { CeRequestStateService } from '../../services/ce-request-state.service';
import { ProjectStageService } from '../../services/project-stage.service';
import { QuestionnaireApiService } from '../../services/questionnaire-api.service';
import { CurrentUserService } from '../../services/current-user.service';
import { DictionariesApiService } from '../../services/dictionaries-api.service';
import { HttpNotificationService } from '../../services/http-notification.service';
import { LoggerService } from '../../services/logger.service';
import { ProjectsApiService, UpdateProjectCommand } from '../../services/projects-api.service';

const SUBMITTED_STATUS_TYPE = 1;
const ISSUED_STATUS_TYPE = 4;

@Component({
  selector: 'app-new-project',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatAutocompleteModule, MatTooltipModule],
  templateUrl: './new-project.html',
  styleUrl: './new-project.scss',
})
export class NewProjectComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly state = inject(CeRequestStateService);
  private readonly dictionariesApi = inject(DictionariesApiService);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly notifications = inject(HttpNotificationService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly logger = inject(LoggerService);
  private readonly questionnaireApi = inject(QuestionnaireApiService);
  private readonly stageService = inject(ProjectStageService);

  protected readonly steps = signal<readonly FlowStep[]>([]);
  protected readonly projectId = signal<number | null>(null);
  protected readonly projectStatusType = signal<number | null>(null);
  protected readonly isSubmittedStatus = computed(() => {
    const status = this.projectStatusType();
    return status === SUBMITTED_STATUS_TYPE || status === ISSUED_STATUS_TYPE;
  });
  protected readonly isProjectManagementTypeReadonly = computed(() => {
    if (this.isSubmittedStatus()) {
      return true;
    }
    const isCeEngineer = this.currentUserService.hasAnyRole(['ce_engineer', 'ce_engineers']);
    if (!isCeEngineer) {
      return false;
    }
    // CE Engineer cannot edit in any status except Draft (null = new project = draft)
    const status = this.projectStatusType();
    return status !== null && status !== 0;
  });
  private readonly originalPmtValue = signal<string>(this.state.draftForm().projectManagementType || '');
  private readonly hasPmtChanged = computed(() => !!this.projectId() && this.formData().projectManagementType !== this.originalPmtValue());
  private pmtPendingCallback: (() => Promise<void>) | null = null;
  protected readonly showPmtConfirmModal = signal(false);
  protected readonly autoDownloadAnswers = signal(false);
  protected readonly isDownloadingAnswers = signal(false);
  protected readonly canAccessResponse = computed(() => {
    const isProjectEngineer = this.currentUserService.hasAnyRole(['project_engineer', 'project_engineers']);
    if (!isProjectEngineer) {
      return true;
    }

    const status = this.projectStatusType();
    return typeof status === 'number' && status >= SUBMITTED_STATUS_TYPE;
  });

  protected readonly shouldShowSaveDraft = computed(() => {
    return !this.projectId();
  });

  protected readonly shouldShowSave = computed(() => {
    return !!this.projectId();
  });

  protected estimateRequestTypes: string[] = [];
  protected projectManagementTypes: string[] = [];
  protected cpdepPhases: string[] = [];
  protected designProgressOptions: string[] = [];
  protected areaOptions: string[] = [];
  protected unitOptions: string[] = [];
  protected ibpOptions: string[] = [];
  protected taYearOptions: string[] = [];
  protected readonly showAllAutocomplete = signal<Record<string, boolean>>({});
  protected readonly detailsAttempted = signal(false);

  protected readonly formData = signal({
    projectTitle: this.state.draftForm().projectTitle || '',
    estimateTitle: this.state.draftForm().estimateTitle || '',
    projectNumber: this.state.draftForm().projectNumber || '',
    projectCode: this.state.draftForm().projectCode || '',
    estimateRequestType: this.state.draftForm().estimateRequestType || '',
    projectManagementType: this.state.draftForm().projectManagementType || '',
    cpdepPhase: this.state.draftForm().cpdepPhase || '',
    anticipatedCompletionDate: this.state.draftForm().anticipatedCompletionDate || '',
    preliminaryCostDeadline: this.state.draftForm().preliminaryCostDeadline || '',
    area: this.state.draftForm().area || '',
    unit: this.state.draftForm().unit || '',
    ibp: this.state.draftForm().ibp || '',
    taYear: this.state.draftForm().taYear || '',
    designProgress: this.state.draftForm().designProgress || '',
  });

  constructor() {
    void this.currentUserService.load();
    this.state.setDetailsRoute('/new-project');
    const paramId = this.route.snapshot.queryParamMap.get('projectId');
    const hasProjectId = !!paramId;
    this.steps.set(
      getNewProjectSteps(
        this.state.flowProgress().questionnaireVisited || hasProjectId,
        this.state.flowProgress().responseVisited || hasProjectId,
        this.canAccessResponse(),
      ),
    );
    void this.stageService.ensureLoaded().catch(error => this.logger.warn('Failed to load project stages', error));
    void this.loadCpdepPhases();
    void this.loadDesignProgresses();
    void this.loadEstimateRequestTypes();
    void this.loadIbpYears();
    void this.loadProjectManagementTypes();
    void this.loadTurnaroundYears();
    void this.loadUnits();
    void this.loadAreas();
    if (paramId) {
      const id = Number(paramId);
      this.projectId.set(id);
      void this.loadProjectById(id);
    }
  }

  protected goBack(): void {
    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate(['/project-team'], queryParams ? { queryParams } : {});
  }

  protected async saveDraft(): Promise<void> {
    if (this.isSubmittedStatus()) {
      return;
    }

    if (this.hasPmtChanged()) {
      this.autoDownloadAnswers.set(true);
      this.pmtPendingCallback = () => this.saveDraft();
      this.showPmtConfirmModal.set(true);
      return;
    }

    this.detailsAttempted.set(true);

    if (!this.hasRequiredDetails()) {
      return;
    }

    this.syncToState();
    const saved = await this.createOrUpdateProject();
    if (!saved) {
      return;
    }

    const createdProjectId = this.projectId();
    if (!createdProjectId) {
      this.notifications.showError('Draft was not saved because project ID was not returned by backend.');
      return;
    }

    this.router.navigate(['/view-request'], { queryParams: { projectId: createdProjectId } });
  }

  protected async save(): Promise<void> {
    if (this.isSubmittedStatus()) {
      return;
    }

    if (this.hasPmtChanged()) {
      this.autoDownloadAnswers.set(true);
      this.pmtPendingCallback = () => this.save();
      this.showPmtConfirmModal.set(true);
      return;
    }

    this.detailsAttempted.set(true);

    if (!this.hasRequiredDetails()) {
      return;
    }

    if (!this.projectId()) {
      return;
    }

    this.syncToState();
    const saved = await this.createOrUpdateProject();
    if (!saved) {
      return;
    }
  }

  protected async goToQuestionnaire(): Promise<void> {
    if (this.isSubmittedStatus()) {
      const queryParams: { projectId?: number | null } = {};

      if (this.projectId()) {
        queryParams.projectId = this.projectId();
      }

      this.router.navigate(['/questionnaire'], { queryParams });
      return;
    }

    if (this.hasPmtChanged()) {
      this.autoDownloadAnswers.set(true);
      this.pmtPendingCallback = () => this.goToQuestionnaire();
      this.showPmtConfirmModal.set(true);
      return;
    }

    this.detailsAttempted.set(true);

    if (!this.hasRequiredDetails()) {
      return;
    }

    this.syncToState();
    const saved = await this.createOrUpdateProject();
    if (!saved) {
      return;
    }

    this.state.markQuestionnaireVisited();
    this.steps.set(getNewProjectSteps(true, this.state.flowProgress().responseVisited, this.canAccessResponse()));

    const queryParams: { projectId?: number | null } = {};
    if (this.projectId()) {
      queryParams.projectId = this.projectId();
    }

    this.router.navigate(['/questionnaire'], { queryParams });
  }

  protected openAutocomplete(field: string, trigger: MatAutocompleteTrigger): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.showAllAutocomplete.update(current => ({ ...current, [field]: true }));
    trigger.openPanel();
  }

  protected closeAutocomplete(field: string): void {
    setTimeout(() => {
      this.showAllAutocomplete.update(current => ({ ...current, [field]: false }));
    }, 120);
  }

  protected onAutocompleteValueChange(field: string, value: string): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    this.showAllAutocomplete.update(current => ({ ...current, [field]: false }));
    this.formData.update(f => ({ ...f, [field]: value }));
  }

  protected onProjectManagementTypeChange(value: string): void {
    if (this.isProjectManagementTypeReadonly()) {
      return;
    }
    this.showAllAutocomplete.update(current => ({ ...current, projectManagementType: false }));
    const cpdepPhase = this.stageService.getCpdepPhase(value);
    const designProgress = this.stageService.getDesignProgress(value);
    this.formData.update(f => ({
      ...f,
      projectManagementType: value,
      cpdepPhase: cpdepPhase || '',
      designProgress: designProgress || '',
    }));
  }

  protected cancelPmtChange(): void {
    this.autoDownloadAnswers.set(false);
    this.showPmtConfirmModal.set(false);
    this.pmtPendingCallback = null;
  }

  protected async confirmPmtChange(): Promise<void> {
    if (this.autoDownloadAnswers()) {
      await this.downloadAnswersAsCsv();
    }
    this.originalPmtValue.set(this.formData().projectManagementType);
    this.showPmtConfirmModal.set(false);
    this.autoDownloadAnswers.set(false);
    const callback = this.pmtPendingCallback;
    this.pmtPendingCallback = null;
    if (callback) {
      await callback();
    }
  }

  private async downloadAnswersAsCsv(): Promise<void> {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.isDownloadingAnswers.set(true);
    try {
      const response = await firstValueFrom(this.questionnaireApi.exportAnswersByProject(projectId));
      const blob = response.body;
      if (!blob) {
        return;
      }
      const fileName = this.getExportFileName(response.headers.get('content-disposition'), projectId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download questionnaire answers:', error);
      const message = await this.extractBlobErrorMessage(error);
      this.notifications.showError(message);
    } finally {
      this.isDownloadingAnswers.set(false);
    }
  }

  private getExportFileName(contentDisposition: string | null, projectId: number): string {
    if (!contentDisposition) {
      return `questionnaire-answers-${projectId}.xlsx`;
    }
    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      try {
        return decodeURIComponent(utfMatch[1]);
      } catch {
        return utfMatch[1];
      }
    }
    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] || `questionnaire-answers-${projectId}.xlsx`;
  }

  private async extractBlobErrorMessage(error: unknown): Promise<string> {
    const fallback = 'Failed to download questionnaire answers.';
    if (error == null || typeof error !== 'object' || !('error' in error)) {
      return fallback;
    }
    const blob = (error as { error?: unknown }).error;
    if (!(blob instanceof Blob)) {
      return fallback;
    }
    try {
      const text = await blob.text();
      const trimmed = text.trim();
      if (!trimmed) {
        return fallback;
      }
      try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        const msg = parsed['message'] ?? parsed['title'] ?? parsed['detail'];
        return typeof msg === 'string' && msg.trim() ? msg.trim() : trimmed;
      } catch {
        return trimmed;
      }
    } catch {
      return fallback;
    }
  }

  protected openDatePicker(event: Event): void {
    if (this.isSubmittedStatus()) {
      return;
    }

    const input = event.target as HTMLInputElement | null;
    if (!input || input.type !== 'date') {
      return;
    }

    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      try {
        pickerInput.showPicker();
      } catch (error) {
        console.error(error);
      }
    }
  }

  protected isStepClickable(step: FlowStep): boolean {
    return !!(step.completed && step.route);
  }

  protected async goToStep(step: FlowStep): Promise<void> {
    if (!this.isStepClickable(step)) {
      return;
    }

    if (step.route === '/questionnaire' && !this.isSubmittedStatus()) {
      if (this.hasPmtChanged()) {
        this.autoDownloadAnswers.set(true);
        this.pmtPendingCallback = () => this.goToStep(step);
        this.showPmtConfirmModal.set(true);
        return;
      }

      this.detailsAttempted.set(true);

      if (!this.hasRequiredDetails()) {
        return;
      }

      this.syncToState();
      const saved = await this.createOrUpdateProject();
      if (!saved) {
        return;
      }
    }

    const id = this.projectId();
    const queryParams = id ? { projectId: id } : undefined;
    this.router.navigate([step.route!], queryParams ? { queryParams } : {});
  }

  protected getAutocompleteOptions(options: readonly string[], field: string, value: string): readonly string[] {
    if (this.showAllAutocomplete()[field]) {
      return options;
    }

    const query = value.trim().toLowerCase();
    if (!query) {
      return options;
    }

    if (options.some(option => option.toLowerCase() === query)) {
      return options;
    }

    return options.filter(option => option.toLowerCase().includes(query));
  }

  private async loadProjectById(id: number): Promise<void> {
    try {
      const dto = await firstValueFrom(this.projectsApi.getProjectById(id));
      this.projectStatusType.set(dto.projectStatusType ?? 0);

      this.state.draftForm.update(d => ({
        ...d,
        projectTeam: dto.projectTeam ?? d.projectTeam,
        afeNumber: dto.afeNumber ?? d.afeNumber,
        isNewProject: dto.isNewProject ?? true,
      }));

      this.formData.set({
        projectTitle: dto.projectTitle ?? '',
        estimateTitle: dto.estimateTitle ?? '',
        projectNumber: dto.projectNumber ?? '',
        projectCode: dto.projectCode ?? '',
        estimateRequestType: dto.estimateRequestType ?? '',
        projectManagementType: dto.projectManagementType ?? '',
        cpdepPhase: dto.cpdepPhase ?? '',
        designProgress: dto.designProgress ?? '',
        anticipatedCompletionDate: dto.projectCompletionDate?.split('T')[0] ?? '',
        preliminaryCostDeadline: dto.deadlineDate?.split('T')[0] ?? '',
        area: dto.area ?? '',
        unit: dto.unit ?? '',
        ibp: dto.ibpYear ?? '',
        taYear: dto.turnaroundYear ?? '',
      });
      this.originalPmtValue.set(dto.projectManagementType ?? '');
    } catch (error) {
      this.logger.warn('Failed to load project details', error);
    }
  }

  private async loadCpdepPhases(): Promise<void> {
    try {
      this.cpdepPhases = await firstValueFrom(this.dictionariesApi.getCpdepPhases());
    } catch (error) {
      this.logger.warn('Failed to load CPDEP phases', error);
      this.cpdepPhases = [];
    }
  }

  private async loadDesignProgresses(): Promise<void> {
    try {
      this.designProgressOptions = await firstValueFrom(this.dictionariesApi.getDesignProgresses());
    } catch (error) {
      console.error(error);
      this.designProgressOptions = [];
    }
  }

  private async loadEstimateRequestTypes(): Promise<void> {
    try {
      this.estimateRequestTypes = await firstValueFrom(this.dictionariesApi.getEstimateRequestTypes());
    } catch (error) {
      console.error(error);
      this.estimateRequestTypes = [];
    }
  }

  private async loadIbpYears(): Promise<void> {
    try {
      this.ibpOptions = await firstValueFrom(this.dictionariesApi.getIbpYears());
    } catch (error) {
      console.error(error);
      this.ibpOptions = [];
    }
  }

  private async loadProjectManagementTypes(): Promise<void> {
    try {
      this.projectManagementTypes = await firstValueFrom(this.dictionariesApi.getProjectManagementTypes());
    } catch (error) {
      console.error(error);
      this.projectManagementTypes = [];
    }
  }

  private async loadTurnaroundYears(): Promise<void> {
    try {
      this.taYearOptions = await firstValueFrom(this.dictionariesApi.getTurnaroundYears());
    } catch (error) {
      console.error(error);
      this.taYearOptions = [];
    }
  }

  private async loadUnits(): Promise<void> {
    try {
      this.unitOptions = await firstValueFrom(this.dictionariesApi.getUnits());
    } catch (error) {
      console.error(error);
      this.unitOptions = [];
    }
  }

  private async loadAreas(): Promise<void> {
    try {
      this.areaOptions = await firstValueFrom(this.dictionariesApi.getAreas());
    } catch (error) {
      console.error(error);
      this.areaOptions = [];
    }
  }

  private async createOrUpdateProject(): Promise<boolean> {
    const existingId = this.projectId();
    if (existingId) {
      return this.updateProjectOnApi(existingId);
    }

    return this.postProjectToApi();
  }

  private async updateProjectOnApi(id: number): Promise<boolean> {
    const f = this.formData();
    const draft = this.state.draftForm();
    const dictionaryIds = await this.resolveProjectDictionaryIds(f);
    if (!dictionaryIds) {
      return false;
    }

    const command: UpdateProjectCommand = {
      projectTeam: draft.projectTeam,
      afeNumber: draft.afeNumber,
      projectTitle: f.projectTitle,
      estimateTitle: f.estimateTitle,
      projectNumber: f.projectNumber,
      projectCode: f.projectCode,
      estimateRequestTypeId: dictionaryIds.estimateRequestTypeId,
      projectManagementTypeId: dictionaryIds.projectManagementTypeId,
      cpdepPhaseId: dictionaryIds.cpdepPhaseId,
      designProgressId: dictionaryIds.designProgressId,
      projectCompletionDate: f.anticipatedCompletionDate ? new Date(f.anticipatedCompletionDate).toISOString() : '',
      deadlineDate: f.preliminaryCostDeadline ? new Date(f.preliminaryCostDeadline).toISOString() : '',
      areaId: dictionaryIds.areaId,
      unitId: dictionaryIds.unitId,
      ibpYearId: dictionaryIds.ibpYearId,
      turnaroundYearId: dictionaryIds.turnaroundYearId,
    };

    try {
      await firstValueFrom(this.projectsApi.updateProject(id, command));
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private async postProjectToApi(): Promise<boolean> {
    const f = this.formData();
    const draft = this.state.draftForm();
    const dictionaryIds = await this.resolveProjectDictionaryIds(f);
    if (!dictionaryIds) {
      return false;
    }

    try {
      const project = await firstValueFrom(
        this.projectsApi.createNewProject({
          projectTeam: draft.projectTeam,
          afeNumber: draft.afeNumber,
          projectTitle: f.projectTitle,
          estimateTitle: f.estimateTitle,
          projectNumber: f.projectNumber,
          projectCode: f.projectCode,
          estimateRequestTypeId: dictionaryIds.estimateRequestTypeId,
          projectManagementTypeId: dictionaryIds.projectManagementTypeId,
          cpdepPhaseId: dictionaryIds.cpdepPhaseId,
          designProgressId: dictionaryIds.designProgressId,
          projectCompletionDate: f.anticipatedCompletionDate ? new Date(f.anticipatedCompletionDate).toISOString() : '',
          deadlineDate: f.preliminaryCostDeadline ? new Date(f.preliminaryCostDeadline).toISOString() : '',
          areaId: dictionaryIds.areaId,
          unitId: dictionaryIds.unitId,
          ibpYearId: dictionaryIds.ibpYearId,
          turnaroundYearId: dictionaryIds.turnaroundYearId,
        }),
      );

      let createdId = 0;
      if (typeof project === 'number') {
        createdId = project;
      } else if (project && typeof project === 'object' && 'id' in project) {
        createdId = Number((project as { id: number }).id);
      }

      if (!Number.isInteger(createdId) || createdId <= 0) {
        this.notifications.showError('Draft was not created: backend did not return a valid project ID.');
        return false;
      }

      this.projectId.set(createdId);
      return true;
    } catch (error) {
      console.error('postProjectToApi error:', error);
      return false;
    }
  }

  private hasRequiredDetails(): boolean {
    const f = this.formData();

    return !!(
      f.projectTitle.trim() &&
      f.estimateTitle.trim() &&
      f.projectNumber.trim() &&
      f.estimateRequestType &&
      f.projectManagementType &&
      f.cpdepPhase &&
      f.designProgress &&
      f.anticipatedCompletionDate &&
      f.preliminaryCostDeadline &&
      f.area &&
      f.unit &&
      f.ibp &&
      f.taYear
    );
  }

  private syncToState(): void {
    const f = this.formData();
    this.state.draftForm.update(d => ({
      ...d,
      estimateTitle: f.estimateTitle,
      estimateRequestType: f.estimateRequestType,
      projectManagementType: f.projectManagementType,
      cpdepPhase: f.cpdepPhase,
      projectTitle: f.projectTitle,
      projectNumber: f.projectNumber,
      projectCode: f.projectCode,
      designProgress: f.designProgress,
      anticipatedCompletionDate: f.anticipatedCompletionDate,
      area: f.area,
      unit: f.unit,
      preliminaryCostDeadline: f.preliminaryCostDeadline,
      ibp: f.ibp,
      taYear: f.taYear,
    }));
  }

  private async resolveProjectDictionaryIds(f: ReturnType<NewProjectComponent['formData']>): Promise<{
    estimateRequestTypeId: number;
    projectManagementTypeId: number;
    cpdepPhaseId: number;
    designProgressId: number;
    areaId: number;
    unitId: number;
    ibpYearId: number;
    turnaroundYearId: number;
  } | null> {
    const [estimateRequestTypes, projectManagementTypes, cpdepPhases, designProgresses, areas, units, ibpYears, turnaroundYears] = await Promise.all([
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('EstimateRequestTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('ProjectManagementTypes')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('CpdepPhases')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('DesignProgresses')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Areas')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('Units')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('IbpYears')),
      firstValueFrom(this.dictionariesApi.getDictionaryItemsCached('TurnaroundYears')),
    ]);

    const ids = {
      estimateRequestTypeId: this.dictionariesApi.findDictionaryId(estimateRequestTypes, f.estimateRequestType),
      projectManagementTypeId: this.dictionariesApi.findDictionaryId(projectManagementTypes, f.projectManagementType),
      cpdepPhaseId: this.dictionariesApi.findDictionaryId(cpdepPhases, f.cpdepPhase),
      designProgressId: this.dictionariesApi.findDictionaryId(designProgresses, f.designProgress),
      areaId: this.dictionariesApi.findDictionaryId(areas, f.area),
      unitId: this.dictionariesApi.findDictionaryId(units, f.unit),
      ibpYearId: this.dictionariesApi.findDictionaryId(ibpYears, f.ibp),
      turnaroundYearId: this.dictionariesApi.findDictionaryId(turnaroundYears, f.taYear),
    };

    if (Object.values(ids).some(id => id <= 0)) {
      this.notifications.showError('One or more dictionary values are outdated. Please reselect dropdown values and try again.');
      return null;
    }

    return ids;
  }

}

